import type {
  BookingRequest,
  BookingResponse,
  CancelRequest,
  ErrorDetails,
  EventTypePublic,
  Hold,
  HoldRequest,
  ManageResponse,
  RescheduleRequest,
  SlotsQuery,
  SlotsResponse,
} from './types';

export class ZwApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: ErrorDetails;

  constructor(code: string, status: number, message: string, details: ErrorDetails = {}) {
    super(message);
    this.name = 'ZwApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export function isZwApiError(error: unknown): error is ZwApiError {
  return error instanceof ZwApiError;
}

export function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

/** Anything thrown while talking to the API, normalised so callers can switch on `code`. */
export function toZwApiError(error: unknown): ZwApiError {
  if (error instanceof ZwApiError) return error;
  return new ZwApiError(
    'network_error',
    0,
    error instanceof Error ? error.message : 'Request failed'
  );
}

export interface ApiOptions {
  /** `''` on hosted pages, `/api/booking` behind a site proxy, or an absolute origin. */
  apiBaseUrl: string;
  widgetKey?: string;
  fetch?: typeof fetch;
  headers?: Record<string, string>;
}

export interface RequestOptions {
  signal?: AbortSignal;
}

export interface EventTypeQuery {
  locale?: string;
  brand?: string;
}

export interface ZwApi {
  getEventType(
    org: string,
    slug: string,
    query?: EventTypeQuery,
    opts?: RequestOptions
  ): Promise<EventTypePublic>;
  getSlots(
    org: string,
    slug: string,
    query: SlotsQuery,
    opts?: RequestOptions
  ): Promise<SlotsResponse>;
  createHold(org: string, body: HoldRequest, opts?: RequestOptions): Promise<Hold>;
  releaseHold(org: string, holdId: string, sessionId: string, opts?: RequestOptions): Promise<void>;
  createBooking(org: string, body: BookingRequest, opts?: RequestOptions): Promise<BookingResponse>;
  getManage(token: string, opts?: RequestOptions): Promise<ManageResponse>;
  reschedule(
    token: string,
    body: RescheduleRequest,
    opts?: RequestOptions
  ): Promise<BookingResponse>;
  cancel(
    token: string,
    body?: CancelRequest,
    opts?: RequestOptions
  ): Promise<{ booking: BookingResponse['booking'] }>;
}

type Query = Record<string, string | undefined>;

function queryString(query?: Query): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, value);
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

const enc = encodeURIComponent;

export function createApi(options: ApiOptions): ZwApi {
  const base = options.apiBaseUrl.replace(/\/+$/, '');

  async function request<T>(
    method: string,
    path: string,
    init: {
      body?: unknown;
      query?: Query;
      headers?: Record<string, string>;
      signal?: AbortSignal;
    } = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...options.headers,
      ...init.headers,
    };
    if (options.widgetKey) headers['X-Zeitwahl-Widget-Key'] = options.widgetKey;
    if (init.body !== undefined) headers['Content-Type'] = 'application/json';

    let res: Response;
    try {
      // Call `fetch` by identifier so the browser binding is never invoked with a foreign `this`.
      res = await (options.fetch ?? fetch)(base + path + queryString(init.query), {
        method,
        headers,
        body: init.body === undefined ? undefined : JSON.stringify(init.body),
        credentials: 'same-origin',
        signal: init.signal,
      });
    } catch (error) {
      if (isAbortError(error)) throw error;
      throw new ZwApiError('network_error', 0, 'The request could not be sent.');
    }

    const text = res.status === 204 ? '' : await res.text();
    let data: unknown = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = null;
      }
    }
    if (!res.ok) {
      const body = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
      const { error, message, ...details } = body;
      throw new ZwApiError(
        typeof error === 'string' ? error : 'http_error',
        res.status,
        typeof message === 'string' ? message : `HTTP ${res.status}`,
        details as ErrorDetails
      );
    }
    return data as T;
  }

  const orgPath = (org: string) => `/v1/public/orgs/${enc(org)}`;

  return {
    getEventType(org, slug, query, opts) {
      return request('GET', `${orgPath(org)}/event-types/${enc(slug)}`, {
        query: { locale: query?.locale, brand: query?.brand },
        signal: opts?.signal,
      });
    },
    getSlots(org, slug, query, opts) {
      return request('GET', `${orgPath(org)}/event-types/${enc(slug)}/slots`, {
        query: {
          from: query.from,
          to: query.to,
          tz: query.tz,
          reschedule: query.reschedule,
          host: query.host,
        },
        signal: opts?.signal,
      });
    },
    async createHold(org, body, opts) {
      const res = await request<{ hold: Hold }>('POST', `${orgPath(org)}/holds`, {
        body,
        signal: opts?.signal,
      });
      return res.hold;
    },
    async releaseHold(org, holdId, sessionId, opts) {
      await request('DELETE', `${orgPath(org)}/holds/${enc(holdId)}`, {
        body: { sessionId },
        headers: { 'X-Zeitwahl-Session': sessionId },
        signal: opts?.signal,
      });
    },
    createBooking(org, body, opts) {
      return request('POST', `${orgPath(org)}/bookings`, { body, signal: opts?.signal });
    },
    getManage(token, opts) {
      return request('GET', `/v1/bookings/${enc(token)}`, { signal: opts?.signal });
    },
    reschedule(token, body, opts) {
      return request('POST', `/v1/bookings/${enc(token)}/reschedule`, {
        body,
        signal: opts?.signal,
      });
    },
    cancel(token, body = {}, opts) {
      return request('POST', `/v1/bookings/${enc(token)}/cancel`, { body, signal: opts?.signal });
    },
  };
}
